/**
 * Automations & notifications master switch (Settings > Automations &
 * notifications).
 *
 * One switch per practice, one per office and one per category decide
 * whether anything automated runs or is sent. Everything is OFF until an
 * administrator turns it on, so nothing reaches a patient, a payment
 * processor or a PMS while a practice is being set up and tested.
 *
 * A send is allowed only when ALL hold:
 *   - the practice master switch is on;
 *   - the office master switch is on (practice-wide work: the practice row);
 *   - the category switch is on;
 *   - for a text or email, the channel category (patient texts / patient
 *     emails) is on too;
 *   - the server's own flag allows it (VELORA_SMS_LIVE, VELORA_EMAIL_LIVE,
 *     VELORA_ANALYTICS_REPORTS, VELORA_PAYMENTS_AUTOPAY, WRITE_READY, ...).
 *
 * A blocked send is recorded as "held: automations off" (`automation_hold`),
 * never as an error, so staff can see what WOULD have been sent.
 *
 * Staff-confirmed bulk sends (select all, review, confirm: worklists, the
 * recall list, statements) need `manual_sends`, never the automated
 * category of the same message (recall, campaigns, payments), so a practice
 * can allow staff bulk sends while every automation stays off. A send that
 * leaves later (scheduled, or pushed past quiet hours) is delivered by a job
 * and needs the automated category.
 *
 * The API gate is `automationGate.allowed()` in
 * apps/api/src/modules/settings/lib/automation-gate.ts; docs/automation-gate.md
 * lists every sender and the call it makes.
 */
import { z } from 'zod';

export const AUTOMATION_KINDS = [
  'patient_texts',
  'patient_emails',
  'ai_calls',
  'reminders',
  'recall',
  'campaigns',
  'manual_sends',
  'reviews',
  'missed_call',
  'after_hours',
  'booking_notices',
  'fill_cascade',
  'payments',
  'staff_reports',
  'portal',
  'paperwork',
  'pms_writes',
] as const;
export type AutomationKind = (typeof AUTOMATION_KINDS)[number];
export const AutomationKindSchema = z.enum(AUTOMATION_KINDS);

/** Channel categories: every text needs `patient_texts`, every email `patient_emails`. */
export const CHANNEL_KIND = { sms: 'patient_texts', email: 'patient_emails' } as const;
export type AutomationChannel = 'sms' | 'email' | 'call' | 'charge' | 'pms' | 'other';
export const AutomationChannelSchema = z.enum(['sms', 'email', 'call', 'charge', 'pms', 'other']);

export interface AutomationKindInfo {
  readonly kind: AutomationKind;
  readonly label: string;
  readonly description: string;
  readonly group: 'Channels' | 'Patient messages' | 'Calls' | 'Money' | 'Staff and systems';
}

export const AUTOMATION_KIND_INFO: readonly AutomationKindInfo[] = [
  {
    kind: 'patient_texts',
    label: 'Patient texts',
    description:
      'Every text to a patient, staff-typed or automated. Each automated text also needs its own category.',
    group: 'Channels',
  },
  {
    kind: 'patient_emails',
    label: 'Patient emails',
    description:
      'Every email to a patient, staff-sent or automated. Each automated email also needs its own category.',
    group: 'Channels',
  },
  {
    kind: 'reminders',
    label: 'Reminders and confirmations',
    description: 'Appointment reminders, confirmation requests and confirmation receipts.',
    group: 'Patient messages',
  },
  {
    kind: 'recall',
    label: 'Recall reminders',
    description: 'Recall and continuing-care reminders for patients who are due.',
    group: 'Patient messages',
  },
  {
    kind: 'campaigns',
    label: 'Campaigns and reactivation',
    description: 'Campaign sends, reactivation, unscheduled treatment and closure notices.',
    group: 'Patient messages',
  },
  {
    kind: 'manual_sends',
    label: 'Staff bulk sends',
    description:
      'Select-all and send from worklists, the recall list and statements, after a confirm. Nothing automatic.',
    group: 'Patient messages',
  },
  {
    kind: 'reviews',
    label: 'Review requests',
    description: 'Texts and emails asking patients for a review after a visit.',
    group: 'Patient messages',
  },
  {
    kind: 'missed_call',
    label: 'Missed-call text-back',
    description: 'The automatic text to a caller whose call was missed.',
    group: 'Patient messages',
  },
  {
    kind: 'after_hours',
    label: 'Automatic text replies',
    description:
      'The after-hours reply and the "we cannot take cancellations by text, please call" reply (Settings > Communications). STOP and HELP replies are always sent.',
    group: 'Patient messages',
  },
  {
    kind: 'booking_notices',
    label: 'Booking notices',
    description: 'Online booking confirmations, changes and cancellation notices.',
    group: 'Patient messages',
  },
  {
    kind: 'fill_cascade',
    label: 'Opening fill texts',
    description:
      'Texts offering a cancelled or broken time to ASAP and recall patients (Settings > Scheduling > Opening fill). Its calls also need AI and automated calls.',
    group: 'Patient messages',
  },
  {
    kind: 'portal',
    label: 'Portal invites and notifications',
    description: 'Patient portal invitations and portal notifications.',
    group: 'Patient messages',
  },
  {
    kind: 'paperwork',
    label: 'Paperwork reminders',
    description: 'Forms and intake links and reminders to finish paperwork.',
    group: 'Patient messages',
  },
  {
    kind: 'ai_calls',
    label: 'AI and automated calls',
    description: 'Outbound calls by the AI receptionist and the schedule fill cascade.',
    group: 'Calls',
  },
  {
    kind: 'payments',
    label: 'Payment links, autopay and plans',
    description:
      'Texted or emailed payment links and statements, autopay and payment-plan charges.',
    group: 'Money',
  },
  {
    kind: 'staff_reports',
    label: 'Emailed staff reports',
    description: 'Scheduled analytics and practice reports emailed to staff.',
    group: 'Staff and systems',
  },
  {
    kind: 'pms_writes',
    label: 'Scheduled PMS writes',
    description:
      'Background jobs that write to the PMS (commlog, confirmations, recall and plan sync). Staff edits are not affected.',
    group: 'Staff and systems',
  },
];

export const AUTOMATION_SETTINGS_ENTITY = 'settings.automation';

export const AutomationCategoriesSchema = z.record(AutomationKindSchema, z.boolean());
export type AutomationCategories = Partial<Record<AutomationKind, boolean>>;

export const AutomationScopeSchema = z.object({
  /** null: the practice-wide row. */
  officeId: z.uuid().nullable(),
  name: z.string(),
  masterOn: z.boolean(),
  /** Every kind, false when never saved. */
  categories: z.record(AutomationKindSchema, z.boolean()),
  version: z.number().int().positive(),
  updatedAt: z.string().nullable(),
  updatedByName: z.string().nullable(),
  canManage: z.boolean(),
});
export type AutomationScope = z.infer<typeof AutomationScopeSchema>;

export const AutomationEnvFlagSchema = z.object({
  flag: z.string(),
  on: z.boolean(),
  kinds: z.array(AutomationKindSchema),
  note: z.string(),
});
export type AutomationEnvFlag = z.infer<typeof AutomationEnvFlagSchema>;

export const AutomationsViewSchema = z.object({
  practice: AutomationScopeSchema,
  offices: z.array(AutomationScopeSchema),
  env: z.array(AutomationEnvFlagSchema),
  /** True while nothing at all can go out (practice master off or every office off). */
  allOff: z.boolean(),
  heldCount: z.number().int().nonnegative(),
});
export type AutomationsView = z.infer<typeof AutomationsViewSchema>;

export const AutomationScopePutSchema = z.object({
  masterOn: z.boolean().optional(),
  categories: z.partialRecord(AutomationKindSchema, z.boolean()).optional(),
  expectedVersion: z.number().int().positive(),
});
export type AutomationScopePut = z.infer<typeof AutomationScopePutSchema>;

export const AutomationStatusSchema = z.object({
  practiceOn: z.boolean(),
  offices: z.array(z.object({ officeId: z.uuid(), on: z.boolean() })),
});
export type AutomationStatus = z.infer<typeof AutomationStatusSchema>;

export const AUTOMATION_BLOCKERS = [
  'practice_master',
  'office_master',
  'category',
  'channel',
  'env',
] as const;
export type AutomationBlocker = (typeof AUTOMATION_BLOCKERS)[number];

export const AutomationHoldSchema = z.object({
  id: z.uuid(),
  officeId: z.uuid().nullable(),
  officeName: z.string().nullable(),
  kind: AutomationKindSchema,
  channel: AutomationChannelSchema,
  blockedBy: z.enum(AUTOMATION_BLOCKERS),
  reason: z.string(),
  source: z.string(),
  summary: z.string(),
  /** What would have been sent (text body, email subject); may name the patient. */
  preview: z.string().nullable(),
  patientId: z.uuid().nullable(),
  patientName: z.string().nullable(),
  attempts: z.number().int().positive(),
  firstHeldAt: z.string(),
  lastHeldAt: z.string(),
});
export type AutomationHold = z.infer<typeof AutomationHoldSchema>;

export const AutomationHoldListSchema = z.object({
  items: z.array(AutomationHoldSchema),
  total: z.number().int().nonnegative(),
});
export type AutomationHoldList = z.infer<typeof AutomationHoldListSchema>;

/** The banner text while automations are off. */
export const AUTOMATIONS_OFF_BANNER = 'Automations are OFF. Nothing is sent to patients.';
/** The plain reason a held send carries. */
export const AUTOMATIONS_OFF_REASON =
  'Held: automations are off (Settings > Automations & notifications).';

export function kindLabel(kind: AutomationKind): string {
  return AUTOMATION_KIND_INFO.find((k) => k.kind === kind)?.label ?? kind;
}
